import { useEffect, useMemo, useState } from "react";
import axios from "axios";

// ---- Read API base from env, with safe fallback.
const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_API_BASE_URL) ||
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_BASE_URL) ||
  (typeof window !== "undefined" && window.API_BASE_URL) ||
  "http://10.150.97.170:3001";

const columns = [
  // first column is the bullet – no header label
  { key: "__bullet__", label: "", widthClass: "w-8" },
  { key: "requestId", label: "Request ID" },
  { key: "createdAt", label: "Created At" },
  { key: "department", label: "Department" },
  { key: "positionType", label: "Position Type" },
  { key: "assigned", label: "Assigned" },
  { key: "positionTitle", label: "Position Title" },
  { key: "status", label: "Status" },
];

function formatDateDDMonYYYY(iso) {
  try {
    // 06 Aug 2025 (note: en-GB gives DD Mon YYYY)
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso ?? "";
  }
}

export function SummaryItemsPage({ endpoint, title }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const url = useMemo(() => `${API_BASE}${endpoint}`, [endpoint]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setErr("Not authenticated");
      setLoading(false);
      return;
    }
    const config = { headers: { Authorization: `Bearer ${token}` } };

    const run = async () => {
      try {
        const res = await axios.get(url, config);
        const list = res.data?.items ?? [];
        list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setItems(list);
      } catch (e) {
        setErr(e?.response?.data?.message || e.message);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [url]);

  return (
    <div className="p-4">
      {/* Title only (no back button) */}
      <h1 className="text-xl font-semibold mb-4">{title}</h1>

      {loading && <div className="text-gray-600">Loading…</div>}
      {err && !loading && <div className="text-red-600">Error: {err}</div>}

      {!loading && !err && (
        <div className="overflow-auto border rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className={`text-left px-4 py-3 border-b border-gray-200 text-gray-700 font-semibold ${c.widthClass || ""}`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-6 text-center text-gray-500">
                    No records found
                  </td>
                </tr>
              )}

              {items.map((row) => (
                <tr key={row.requestId} className="hover:bg-gray-50">
                  {/* bullet cell */}
                  <td className="px-4 py-3 border-b border-gray-100">
                    <span
                      aria-hidden="true"
                      className="inline-block align-middle w-3 h-3 rounded-full border border-gray-400"
                    />
                  </td>

                  {/* Request ID (plain text, not a link) */}
                  <td className="px-4 py-3 border-b border-gray-100 text-gray-900">
                    {row.requestId}
                  </td>

                  <td className="px-4 py-3 border-b border-gray-100">
                    {formatDateDDMonYYYY(row.createdAt)}
                  </td>
                  <td className="px-4 py-3 border-b border-gray-100">{row.department}</td>
                  <td className="px-4 py-3 border-b border-gray-100">{row.positionType ?? "—"}</td>
                  <td className="px-4 py-3 border-b border-gray-100">{row.assigned ?? "—"}</td>
                  <td className="px-4 py-3 border-b border-gray-100">{row.positionTitle ?? "—"}</td>
                  <td className="px-4 py-3 border-b border-gray-100">{row.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Route wrappers
export const OngoingHiresTablePage = () => (
  <SummaryItemsPage title="Ongoing Hires" endpoint={`/api/requests/ongoing-hires/summary/items`} />
);

export const CompletedHiresTablePage = () => (
  <SummaryItemsPage title="Completed Hires" endpoint={`/api/requests/completed-hires/summary/items`} />
);

export const DiscardedHiresTablePage = () => (
  <SummaryItemsPage title="Discarded Hires" endpoint={`/api/requests/discarded-hires/summary/items`} />
);
